// ─────────────────────────────────────────────
//  normalizeName — the key that lets a person from the contact SHEET meet the
//  same person in the app's own `staff` pool.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The sheet carries no uid, no email and no employee number, so the ONLY join
//  available is the name (TEAM-CONTRACT §4). The two sides were typed years
//  apart by different hands: the sheet writes "Osunsanmi / Babajide(Michael)",
//  the staff pool writes "Josh Larson" for one person and "Josh Larson 7 REQ"
//  for the same person on a different day. This function is what makes those
//  agree — and it runs at READ time in the page. Nothing here is ever written
//  back to `staff` or `users`.
//
//  ⚠️ THE SHIFT-CODE VOCABULARY IS NOT DEFINED HERE. It is imported from
//  domain/schedule (SHIFT_CODES), the same list the assignment dropdowns and
//  the sheet-import CRNA-cell reader uses. A second, local copy would drift
//  the first time the department adds a shift, and the drift would surface as
//  people silently losing their badges — no error, no test failure.
//
//  ⚠️ TOKEN DROPPING NEVER EMPTIES A NAME. If every token looks like a code the
//  original is kept: an unmatched person renders fine (§4 — "no match is
//  normal"), a person normalised to "" would collide with every other blank.
//
//  ⚠️ Loosening this MERGES PEOPLE. Two clinicians shown as one, with one
//  phone number, is a worse failure than an unmatched row. Widen it only with
//  a real name variant in hand, and add it to normalizeName.test.js.
//
//  ⚠️ WHERE THE LINE IS. Everything this function does is PUNCTUATION AND
//  DECORATION folding: accents, parentheticals, apostrophes, periods, commas,
//  whitespace, trailing shift/annotation tokens. All of it is safe for one
//  reason — it only removes characters that carry no distinguishing
//  information, so it can never bring two DIFFERENT names together.
//  "O'Hara" and "OHara" are one person spelled two ways; "Mitch O'Hara" and
//  "Mitchell O'Hara" stay two people, because the difference between them is
//  LETTERS, and no rule here touches letters.
//  The next widening will be tempting to take past that line — a nickname map
//  (Mitch → Mitchell), a token-subset match, an initials rule, dropping a whole
//  name token. Those operate on letters, they WILL merge two real clinicians,
//  and they do not belong here. If you need one, it belongs behind a human
//  confirming the match, not in a join key that runs unattended.
//
//  Pure — no Firebase, no React, no network.
// ─────────────────────────────────────────────

import { SHIFT_CODES } from "../../domain/schedule";

const SHIFT_TOKENS = new Set(SHIFT_CODES.map((c) => c.toLowerCase()));

// Trailing bookkeeping words the staff pool appends to a name. Kept
// deliberately short: every entry here is a token we have SEEN, because each
// one is a chance to merge two real people who happen to share a surname.
export const NAME_ANNOTATION_TOKENS = new Set(["req", "reqs"]);

// A bare 1–2 digit token: covers the shifts that never made it into
// SHIFT_CODES ("6", "10"). No surname is a number.
const BARE_NUMBER = /^\d{1,2}$/;

const isDroppable = (token) =>
  SHIFT_TOKENS.has(token) || NAME_ANNOTATION_TOKENS.has(token) || BARE_NUMBER.test(token);

/**
 * Normalise a display name into the cross-reference match key (§4):
 * lowercase → strip accents → strip anything in parentheses → delete
 * apostrophes → flatten periods and commas → collapse whitespace → drop
 * trailing shift/annotation tokens.
 *
 * Compare two names by `normalizeName(a) === normalizeName(b)`; an empty
 * result never matches anything, which is the correct answer for a nameless
 * cell.
 *
 * @param {*} s
 * @returns {string}
 */
export function normalizeName(s) {
  const base = String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining accents
    .toLowerCase()
    .replace(/\([^)]*\)?/g, " ") // strip parentheticals, terminated or not
    // Apostrophes are DELETED, not spaced: "O'Brien" is ONE word, and spacing
    // it would file the same person under a different key from the staff doc
    // ("o brien" vs "obrien"). Live names: O'Hara, Chris B', Chris G'.
    .replace(/['’]/g, "")
    // Periods and commas flatten to a space — the staff pool carries
    // "Shivam P." and "Larson, Josh"; a trailing period must not reach the key.
    .replace(/[.,]/g, " ")
    // Collapse LAST, so the substitutions above cannot leave double spaces and
    // isDroppable always sees clean tokens.
    .replace(/\s+/g, " ")
    .trim();

  if (!base) return "";

  const tokens = base.split(" ");
  // Peel trailing codes one at a time — "josh larson 7 req" sheds TWO — but
  // never take the last surviving token.
  while (tokens.length > 1 && isDroppable(tokens[tokens.length - 1])) tokens.pop();
  return tokens.join(" ");
}
